import { Col, Dropdown, DropdownButton, Form, InputGroup, Row } from 'react-bootstrap';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { DatePicker } from '@Components/DatePicker';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { defaultTimeframe, WtoRule, WtoTimeframe } from '../../wto-form-context';
import {
  getParamFunnelStep,
  getParamTestName,
  getParamVersionNumber,
  updateParamFunnelStep,
  updateParamTestName,
  updateParamVersionNumber
} from '../../wto-utils';
import {
  TIMEFRAME_MAX_YEAR,
  TIMEFRAME_MIN_YEAR,
  TIMEZONE_MAP_KEYS
} from './rules-form-modal-constants';
import { TimeSelection } from './TimeSelection';

interface TimeframeSectionProps {
  // timeframe_enabled implies timeframe is set — see the render guard in RulesFormModal.tsx.
  newRule: WtoRule & { timeframe: WtoTimeframe };
  wtoFormObj: Plover;
  readOnly: boolean;
  today: string;
  handleTimeframeDates: (dates: { startDate: string; endDate: string }) => void;
  handleTimeframeTime: (
    value: string | null,
    type: 'hour' | 'minute',
    bound: 'start' | 'end'
  ) => void;
  handleTimeframePeriod: (period: 'am' | 'pm' | null, bound: 'start' | 'end') => void;
  handleTimeframeTimezone: (timezone: string | null) => void;
  toggleTimeframeParam: () => void;
  updateTimeframeId: (value: string) => void;
}

export const TimeframeSection = ({
  newRule,
  wtoFormObj,
  readOnly,
  today,
  handleTimeframeDates,
  handleTimeframeTime,
  handleTimeframePeriod,
  handleTimeframeTimezone,
  toggleTimeframeParam,
  updateTimeframeId
}: TimeframeSectionProps) => (
  <Row>
    <Form.Group as={Col} className='rule-editor timeframe-wrapper'>
      <span className='rule-header'>Time Frame</span>
      <div className='dateSelection'>
        <div className='date-range-wrapper'>
          Start and End Dates:
          <DatePicker
            disabled={readOnly}
            // Lifts the default cutoff at yesterday; minYear/maxYear still bound the range.
            isOutsideRange={() => false}
            minYear={TIMEFRAME_MIN_YEAR}
            maxYear={TIMEFRAME_MAX_YEAR}
            range={{
              start: newRule.timeframe.startDateTime.split('T')[0] || today,
              end: newRule.timeframe.endDateTime.split('T')[0] || today
            }}
            onChange={({ start, end }) => handleTimeframeDates({ startDate: start, endDate: end })}
          />
        </div>
        <div className='time-range-wrapper'>
          Start Time:{' '}
          <TimeSelection
            readOnly={readOnly}
            handleHour={hour => handleTimeframeTime(hour, 'hour', 'start')}
            handleMinute={minute => handleTimeframeTime(minute, 'minute', 'start')}
            handleAmpm={period => handleTimeframePeriod(period, 'start')}
            timestamp={newRule.timeframe.startDateTime}
          />
          End Time:{' '}
          <TimeSelection
            readOnly={readOnly}
            handleHour={hour => handleTimeframeTime(hour, 'hour', 'end')}
            handleMinute={minute => handleTimeframeTime(minute, 'minute', 'end')}
            handleAmpm={period => handleTimeframePeriod(period, 'end')}
            timestamp={newRule.timeframe.endDateTime}
          />
          <DropdownButton
            disabled={readOnly}
            className='condition-dropdown'
            title={newRule.timeframe.time_zone ? newRule.timeframe.time_zone : 'Select Timezone'}
          >
            {TIMEZONE_MAP_KEYS.map(timezone => (
              <Dropdown.Item
                key={timezone}
                eventKey={timezone}
                onClick={() => handleTimeframeTimezone(timezone)}
              >
                {timezone}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
        <div className='timeframe-utm-wrapper'>
          <ToggleSwitch
            disabled={readOnly}
            label='Timeframe Route Tracking URL Parameter'
            checked={newRule.timeframe.timeframe_utm_test_id_enabled}
            onChange={toggleTimeframeParam}
          />
          {newRule.timeframe.timeframe_utm_test_id_enabled && (
            <>
              <p>
                All web traffic will be tagged with the <strong>{wtoFormObj.utm_test_id}</strong>{' '}
                parameter outside of the Rule Time Frame. During the time frame specified above, all
                Web Traffic will be tagged using the following Tracking URL Parameter:
              </p>
              <div className='utm-field-container'>
                utm_
                <Form.Control
                  disabled={readOnly}
                  type='text'
                  placeholder='Funnel Step'
                  value={getParamFunnelStep(newRule.timeframe.utm_test_id)}
                  onChange={e =>
                    updateTimeframeId(
                      updateParamFunnelStep(
                        e.target.value,
                        newRule.timeframe.utm_test_id
                          ? newRule.timeframe.utm_test_id
                          : defaultTimeframe.utm_test_id
                      )
                    )
                  }
                />
                _exp=
                <InputGroup>
                  <Form.Control
                    className='testName'
                    disabled={readOnly}
                    type='text'
                    placeholder='Test Name'
                    value={getParamTestName(newRule.timeframe.utm_test_id)}
                    onChange={e =>
                      updateTimeframeId(
                        updateParamTestName(
                          e.target.value,
                          newRule.timeframe.utm_test_id
                            ? newRule.timeframe.utm_test_id
                            : defaultTimeframe.utm_test_id
                        )
                      )
                    }
                  />
                  {/* TODO(DEV-24309): no input for a suffix yet -- see wto-utils.ts. */}
                  <Form.Control
                    className='version'
                    disabled={readOnly}
                    type='number'
                    placeholder='Version'
                    value={getParamVersionNumber(newRule.timeframe.utm_test_id)}
                    onChange={e =>
                      updateTimeframeId(
                        updateParamVersionNumber(
                          Number(e.target.value),
                          newRule.timeframe.utm_test_id
                            ? newRule.timeframe.utm_test_id
                            : defaultTimeframe.utm_test_id
                        )
                      )
                    }
                  />
                </InputGroup>
              </div>
            </>
          )}
        </div>
      </div>
    </Form.Group>
  </Row>
);
